import React from 'react';
import { Easing } from 'remotion';
import { LayerItem } from './base';
import { ThemeSystemConfig } from '../../clipCreator/remotion/Components/Theme/Theme';
import { AspectRatio } from '../AspectRatio';
import { TimelineDefaults } from '../TimelineDefaults';
import { calculateAnimationTransform } from '../animations/implementations';

/**
 * Animation timing configuration
 */
export interface AnimationTimingConfig {
    duration: number; // in seconds
    delay?: number; // in seconds
    easing?: EasingType;
}

/**
 * Animation easing types - using Remotion's built-in easing functions
 */
export type EasingType = 
    | 'linear'
    | 'ease'
    | 'ease-in'
    | 'ease-out'
    | 'ease-in-out'
    | 'ease-in-quad'
    | 'ease-out-quad'
    | 'ease-in-out-quad'
    | 'ease-in-cubic'
    | 'ease-out-cubic'
    | 'ease-in-out-cubic'
    | 'ease-in-quart'
    | 'ease-out-quart'
    | 'ease-in-out-quart'
    | 'ease-in-quint'
    | 'ease-out-quint'
    | 'ease-in-out-quint'
    | 'ease-in-sine'
    | 'ease-out-sine'
    | 'ease-in-out-sine'
    | 'ease-in-expo'
    | 'ease-out-expo'
    | 'ease-in-out-expo'
    | 'ease-in-circ'
    | 'ease-out-circ'
    | 'ease-in-out-circ'
    | 'ease-in-back'
    | 'ease-out-back'
    | 'ease-in-out-back'
    | 'ease-in-elastic'
    | 'ease-out-elastic'
    | 'ease-in-out-elastic'
    | 'ease-in-bounce'
    | 'ease-out-bounce'
    | 'ease-in-out-bounce';

/**
 * Animation direction for slide and other directional animations
 */
export type AnimationDirection = 'top' | 'bottom' | 'left' | 'right';

/**
 * Animation timing mode controls when animations occur relative to element boundaries
 * 
 * Examples with a 2-second animation on a 5-second element:
 * 
 * ENTRANCE ANIMATIONS:
 * - 'before-at': Animation runs from -2s to 0s, element fully visible at 0s (default)
 * - 'at-after': Animation runs from 0s to 2s, element becomes visible during scheduled time
 * - 'centered': Animation runs from -1s to 1s, element transitions through scheduled start
 * 
 * EXIT ANIMATIONS:
 * - 'before-at': Animation runs from 3s to 5s, element fully hidden at 5s (default)
 * - 'at-after': Animation runs from 5s to 7s, element visible beyond scheduled time
 * - 'centered': Animation runs from 4s to 6s, element transitions through scheduled end
 */
export type AnimationTimingMode = 
    | 'before-at'    // Animation starts before boundary and completes AT boundary (default)
    | 'at-after'     // Animation starts AT boundary and extends after boundary
    | 'centered';    // Animation is centered on boundary (half before, half after)

/**
 * Centralized animation timing mode defaults.
 * These constants ensure consistent timing behavior across all animation systems.
 */
export const ANIMATION_TIMING_DEFAULTS = {
    ENTRANCE: 'before-at' as AnimationTimingMode,  // Default for entrance animations
    EXIT: 'before-at' as AnimationTimingMode,      // Default for exit animations (FIXED: was inconsistent)
} as const;

/**
 * Core animation types
 */
export type AnimationType = 
    | 'none'
    | 'fade'
    | 'slide'
    | 'slide-up'
    | 'slide-down'
    | 'slide-left'
    | 'slide-right'
    | 'scale'
    | 'scale-up'
    | 'scale-down'
    | 'rotate'
    | 'bounce'
    | 'elastic'
    | 'fadeOutUp'   // Directional fade animation
    | 'fadeInDown'  // Directional fade animation
    | 'slideIn'     // Legacy - use 'slide' instead
    | 'slideOut'    // Legacy - use 'slide' instead
    | 'scaleIn'     // Legacy - use 'scale' instead
    | 'scaleOut'    // Legacy - use 'scale' instead
    | 'fadeIn'      // Legacy - use 'fade' instead
    | 'fadeOut'     // Legacy - use 'fade' instead
    | 'typewriter'  // TODO: Implement
    | 'stagger';    // TODO: Implement


/**
 * Animation configuration interface
 * 
 * BACKWARD COMPATIBILITY: The timingMode property is optional and defaults to 'before-at'
 * to maintain existing animation behavior. All existing AnimationConfig objects will
 * continue to work without modification.
 */
export interface AnimationConfig {
    type: AnimationType;
    timing: AnimationTimingConfig;
    timingMode?: AnimationTimingMode; // When animation occurs relative to element boundaries (defaults to 'before-at')
    direction?: AnimationDirection;
    scale?: number;
    target?: string; // for targeting specific elements within component
    intensity?: number; // 0-1 scale for animation intensity
    properties?: Record<string, any>; // additional animation-specific properties
    
}

/**
 * Sound effect configuration - Enhanced with timing constraints
 */
export interface SoundConfig {
    sound: string; // Sound file path or ID
    timing?: {
        delay?: number;     // Delay from animation start (seconds)
        trimStart?: number; // Where in audio file to begin (seconds)
        volume?: number;    // 0-1 scale, default: 1
        fadeIn?: number;    // Optional: fade in duration (seconds)
        fadeOut?: number;   // Optional: fade out duration (seconds)
    };
    metadata?: {
        originalDuration?: number;    // For UI warnings and validation
        recommendedDuration?: number; // Optimal usage duration for this sound
        category?: 'entrance' | 'exit' | 'transition' | 'ui';
        energy?: 'low' | 'medium' | 'high';  // For smart matching
    };
}

/**
 * AnimatedItem interface - follows the exact same pattern as PositionedItem
 * This enables any layer item to have integrated animations
 */
export interface AnimatedItem extends LayerItem {
    id: string;
    startAt: number;
    duration?: number;
    entranceAnimation?: AnimationConfig;
    exitAnimation?: AnimationConfig;
    entranceSoundEffect?: SoundConfig;
    exitSoundEffect?: SoundConfig;
    customAnimations?: AnimationConfig[];
    customSoundEffects?: SoundConfig[];
}

/**
 * Abstract implementation class following the PositionedItemImpl pattern
 * Provides default animation behavior and properties
 */
export abstract class AnimatedItemImpl implements AnimatedItem {
    id: string;
    startAt: number;
    duration?: number;
    entranceAnimation?: AnimationConfig;
    exitAnimation?: AnimationConfig;
    entranceSoundEffect?: SoundConfig;
    exitSoundEffect?: SoundConfig;
    customAnimations: AnimationConfig[];
    customSoundEffects: SoundConfig[];

    constructor(item: Omit<AnimatedItem, 'render'>) {
        this.id = item.id;
        this.startAt = item.startAt;
        this.duration = item.duration;
        this.entranceAnimation = item.entranceAnimation;
        this.exitAnimation = item.exitAnimation;
        this.entranceSoundEffect = item.entranceSoundEffect;
        this.exitSoundEffect = item.exitSoundEffect;
        this.customAnimations = item.customAnimations ?? [];
        this.customSoundEffects = item.customSoundEffects ?? [];
    }

    /**
     * Calculate animation progress for entrance animations
     * Returns progress value between 0 and 1
     * 
     * Supports three timing modes:
     * - 'before-at': Animation starts before startAt and completes BY startAt (default)
     * - 'at-after': Animation starts AT startAt and extends after startAt
     * - 'centered': Animation is centered on startAt (half before, half after)
     */
    protected calculateEntranceProgress(currentFrame: number, fps: number): number {
        if (!this.entranceAnimation) return 1;
        
        const itemStartFrame = this.startAt * fps;
        const animationDuration = this.entranceAnimation.timing.duration;
        const delayFrames = (this.entranceAnimation.timing.delay ?? 0) * fps;
        const totalAnimationFrames = animationDuration * fps;
        const timingMode = this.entranceAnimation.timingMode ?? ANIMATION_TIMING_DEFAULTS.ENTRANCE; // Default for backward compatibility
        
        let animationStartFrame: number;
        
        switch (timingMode) {
            case 'before-at':
                // Animation starts BEFORE the item's scheduled start time and completes BY startAt
                animationStartFrame = itemStartFrame - totalAnimationFrames - delayFrames;
                break;
            case 'at-after':
                // Animation starts AT the item's scheduled start time and extends after
                animationStartFrame = itemStartFrame + delayFrames;
                break;
            case 'centered':
                // Animation is centered on the item's scheduled start time
                animationStartFrame = itemStartFrame - (totalAnimationFrames / 2) - delayFrames;
                break;
        }
        
        const framesSinceAnimationStart = currentFrame - animationStartFrame;
        
        if (framesSinceAnimationStart < 0) return 0; // Animation hasn't started yet
        if (framesSinceAnimationStart >= totalAnimationFrames) return 1; // Animation complete
        
        return Math.min(1, framesSinceAnimationStart / totalAnimationFrames);
    }

    /**
     * Calculate animation progress for exit animations
     * Returns progress value between 0 and 1
     * 
     * Supports three timing modes:
     * - 'before-at': Animation starts before endAt and completes BY endAt (default)
     * - 'at-after': Animation starts AT endAt and extends after endAt
     * - 'centered': Animation is centered on endAt (half before, half after)
     */
    protected calculateExitProgress(currentFrame: number, fps: number): number {
        if (!this.exitAnimation || !this.duration) return 0;
        
        const startFrame = this.startAt * fps;
        const itemDurationFrames = this.duration * fps;
        const itemEndFrame = startFrame + itemDurationFrames;
        const animationDuration = this.exitAnimation.timing.duration;
        const delayFrames = (this.exitAnimation.timing.delay ?? 0) * fps;
        const totalAnimationFrames = animationDuration * fps;
        const timingMode = this.exitAnimation.timingMode ?? ANIMATION_TIMING_DEFAULTS.EXIT; // Default for backward compatibility
        
        let exitStartFrame: number;
        
        switch (timingMode) {
            case 'before-at':
                // Exit animation starts BEFORE the item ends and completes BY endAt
                exitStartFrame = itemEndFrame - totalAnimationFrames - delayFrames;
                break;
            case 'at-after':
                // Exit animation starts AT the item's scheduled end time and extends after
                exitStartFrame = itemEndFrame + delayFrames;
                break;
            case 'centered':
                // Exit animation is centered on the item's scheduled end time
                exitStartFrame = itemEndFrame - (totalAnimationFrames / 2) - delayFrames;
                break;
        }
        
        const framesSinceExitStart = currentFrame - exitStartFrame;
        
        if (framesSinceExitStart < 0) return 0; // Exit animation hasn't started
        if (framesSinceExitStart >= totalAnimationFrames) return 1; // Exit animation complete
        
        return Math.min(1, framesSinceExitStart / totalAnimationFrames);
    }

    /**
     * Apply easing function to progress value using Remotion's built-in easing functions
     */
    protected applyEasing(progress: number, easing: EasingType = 'ease-out-cubic'): number {
        // Map our easing types to Remotion's easing functions
        switch (easing) {
            case 'linear':
                return Easing.linear(progress);
            case 'ease':
                return Easing.bezier(0.25, 0.1, 0.25, 1)(progress);
            case 'ease-in':
                return Easing.in(Easing.ease)(progress);
            case 'ease-out':
                return Easing.out(Easing.ease)(progress);
            case 'ease-in-out':
                return Easing.inOut(Easing.ease)(progress);
            
            // Quad easing
            case 'ease-in-quad':
                return Easing.in(Easing.quad)(progress);
            case 'ease-out-quad':
                return Easing.out(Easing.quad)(progress);
            case 'ease-in-out-quad':
                return Easing.inOut(Easing.quad)(progress);
            
            // Cubic easing
            case 'ease-in-cubic':
                return Easing.in(Easing.cubic)(progress);
            case 'ease-out-cubic':
                return Easing.out(Easing.cubic)(progress);
            case 'ease-in-out-cubic':
                return Easing.inOut(Easing.cubic)(progress);
            
            // Quart easing
            case 'ease-in-quart':
                return Easing.in(Easing.poly(4))(progress);
            case 'ease-out-quart':
                return Easing.out(Easing.poly(4))(progress);
            case 'ease-in-out-quart':
                return Easing.inOut(Easing.poly(4))(progress);
            
            // Quint easing
            case 'ease-in-quint':
                return Easing.in(Easing.poly(5))(progress);
            case 'ease-out-quint':
                return Easing.out(Easing.poly(5))(progress);
            case 'ease-in-out-quint':
                return Easing.inOut(Easing.poly(5))(progress);
            
            // Sine easing
            case 'ease-in-sine':
                return Easing.in(Easing.sin)(progress);
            case 'ease-out-sine':
                return Easing.out(Easing.sin)(progress);
            case 'ease-in-out-sine':
                return Easing.inOut(Easing.sin)(progress);
            
            // Expo easing
            case 'ease-in-expo':
                return Easing.in(Easing.exp)(progress);
            case 'ease-out-expo':
                return Easing.out(Easing.exp)(progress);
            case 'ease-in-out-expo':
                return Easing.inOut(Easing.exp)(progress);
            
            // Circ easing
            case 'ease-in-circ':
                return Easing.in(Easing.circle)(progress);
            case 'ease-out-circ':
                return Easing.out(Easing.circle)(progress);
            case 'ease-in-out-circ':
                return Easing.inOut(Easing.circle)(progress);
            
            // Back easing (overshoot)
            case 'ease-in-back':
                return Easing.in(Easing.back())(progress);
            case 'ease-out-back':
                return Easing.out(Easing.back())(progress);
            case 'ease-in-out-back':
                return Easing.inOut(Easing.back())(progress);
            
            // Elastic easing
            case 'ease-in-elastic':
                return Easing.in(Easing.elastic())(progress);
            case 'ease-out-elastic':
                return Easing.out(Easing.elastic())(progress);
            case 'ease-in-out-elastic':
                return Easing.inOut(Easing.elastic())(progress);
            
            // Bounce easing
            case 'ease-in-bounce':
                return Easing.in(Easing.bounce)(progress);
            case 'ease-out-bounce':
                return Easing.out(Easing.bounce)(progress);
            case 'ease-in-out-bounce':
                return Easing.inOut(Easing.bounce)(progress);
            
            default:
                // Default to ease-out-cubic for modern feel
                return Easing.out(Easing.cubic)(progress);
        }
    }

    /**
     * Get animation transform styles based on current progress
     * Now uses the new animation utility system for consistent, hardware-accelerated animations
     */
    protected getAnimationTransform(
        animation: AnimationConfig, 
        progress: number,
        isExit: boolean = false
    ): React.CSSProperties {
        // Import animation calculation utilities (moved to top-level import)
        // const { calculateAnimationTransform } = require('../../animations/implementations');
        
        const easedProgress = this.applyEasing(progress, animation.timing.easing ?? 'ease-out-cubic');
        const direction = isExit ? 'exit' : 'entrance';
        
        // Use new animation system for supported types
        try {
            return calculateAnimationTransform(animation.type, easedProgress, direction);
        } catch (error) {
            // Fallback to legacy implementation for unsupported types
            console.warn(`Animation type "${animation.type}" not supported by new system, using legacy implementation`);
            return this.getLegacyAnimationTransform(animation, easedProgress, isExit);
        }
    }

    /**
     * Legacy animation transform implementation for backward compatibility
     * @deprecated Use new animation system instead
     */
    private getLegacyAnimationTransform(
        animation: AnimationConfig,
        easedProgress: number,
        isExit: boolean
    ): React.CSSProperties {
        switch (animation.type) {
            case 'fadeIn':
                return { opacity: isExit ? 1 - easedProgress : easedProgress };
            
            case 'fadeOut':
                return { opacity: isExit ? easedProgress : 1 - easedProgress };
            
            case 'slideIn':
                const slideProgress = isExit ? 1 - easedProgress : easedProgress;
                const slideDistance = 100; // percentage
                switch (animation.direction) {
                    case 'left':
                        return { transform: `translateX(${(1 - slideProgress) * -slideDistance}%)` };
                    case 'right':
                        return { transform: `translateX(${(1 - slideProgress) * slideDistance}%)` };
                    case 'top':
                        return { transform: `translateY(${(1 - slideProgress) * -slideDistance}%)` };
                    case 'bottom':
                        return { transform: `translateY(${(1 - slideProgress) * slideDistance}%)` };
                    default:
                        return { transform: `translateX(${(1 - slideProgress) * -slideDistance}%)` };
                }
            
            case 'slideOut':
                const slideOutProgress = isExit ? easedProgress : 1 - easedProgress;
                const slideOutDistance = 100;
                switch (animation.direction) {
                    case 'left':
                        return { transform: `translateX(${slideOutProgress * -slideOutDistance}%)` };
                    case 'right':
                        return { transform: `translateX(${slideOutProgress * slideOutDistance}%)` };
                    case 'top':
                        return { transform: `translateY(${slideOutProgress * -slideOutDistance}%)` };
                    case 'bottom':
                        return { transform: `translateY(${slideOutProgress * slideOutDistance}%)` };
                    default:
                        return { transform: `translateX(${slideOutProgress * slideOutDistance}%)` };
                }
            
            case 'scaleIn':
                const scaleProgress = isExit ? 1 - easedProgress : easedProgress;
                const minScale = animation.properties?.minScale ?? 0.8;
                const scale = minScale + (1 - minScale) * scaleProgress;
                return { transform: `scale(${scale})` };
            
            case 'scaleOut':
                const scaleOutProgress = isExit ? easedProgress : 1 - easedProgress;
                const maxScale = animation.properties?.maxScale ?? 1.2;
                const scaleOut = 1 + (maxScale - 1) * scaleOutProgress;
                return { transform: `scale(${scaleOut})` };
            
            case 'typewriter':
            case 'stagger':
                // TODO: Implement typewriter and stagger animations
                return {};
            
            default:
                return {};
        }
    }

    /**
     * Check if entrance sound effect should be triggered at the current frame
     * Respects animation timing modes for proper sound synchronization
     */
    shouldTriggerEntranceSoundEffect(currentFrame: number, fps: number): boolean {
        if (!this.entranceSoundEffect) return false;
        
        const delayFrames = (this.entranceSoundEffect.timing?.delay ?? 0) * fps;
        let triggerFrame: number;
        
        if (this.entranceAnimation) {
            // Sound triggers when entrance animation starts, respecting timing mode
            const itemStartFrame = this.startAt * fps;
            const animationDuration = this.entranceAnimation.timing.duration;
            const animationDelayFrames = (this.entranceAnimation.timing.delay ?? 0) * fps;
            const totalAnimationFrames = animationDuration * fps;
            const timingMode = this.entranceAnimation.timingMode ?? ANIMATION_TIMING_DEFAULTS.ENTRANCE;
            
            let animationStartFrame: number;
            
            switch (timingMode) {
                case 'before-at':
                    animationStartFrame = itemStartFrame - totalAnimationFrames - animationDelayFrames;
                    break;
                case 'at-after':
                    animationStartFrame = itemStartFrame + animationDelayFrames;
                    break;
                case 'centered':
                    animationStartFrame = itemStartFrame - (totalAnimationFrames / 2) - animationDelayFrames;
                    break;
            }
            
            triggerFrame = animationStartFrame + delayFrames;
        } else {
            // No entrance animation, trigger at item start
            triggerFrame = this.startAt * fps + delayFrames;
        }
        
        return Math.abs(triggerFrame - currentFrame) < 0.5; // Half-frame tolerance
    }

    /**
     * Check if exit sound effect should be triggered at the current frame
     * Respects animation timing modes for proper sound synchronization
     */
    shouldTriggerExitSoundEffect(currentFrame: number, fps: number): boolean {
        if (!this.exitSoundEffect || !this.duration) return false;
        
        const delayFrames = (this.exitSoundEffect.timing?.delay ?? 0) * fps;
        let triggerFrame: number;
        
        if (this.exitAnimation) {
            // Sound triggers when exit animation starts, respecting timing mode
            const itemStartFrame = this.startAt * fps;
            const itemDurationFrames = this.duration * fps;
            const itemEndFrame = itemStartFrame + itemDurationFrames;
            const animationDuration = this.exitAnimation.timing.duration;
            const animationDelayFrames = (this.exitAnimation.timing.delay ?? 0) * fps;
            const totalAnimationFrames = animationDuration * fps;
            const timingMode = this.exitAnimation.timingMode ?? ANIMATION_TIMING_DEFAULTS.EXIT;
            
            let exitStartFrame: number;
            
            switch (timingMode) {
                case 'before-at':
                    exitStartFrame = itemEndFrame - totalAnimationFrames - animationDelayFrames;
                    break;
                case 'at-after':
                    exitStartFrame = itemEndFrame + animationDelayFrames;
                    break;
                case 'centered':
                    exitStartFrame = itemEndFrame - (totalAnimationFrames / 2) - animationDelayFrames;
                    break;
            }
            
            triggerFrame = exitStartFrame + delayFrames;
        } else {
            // No exit animation, trigger at item end
            triggerFrame = (this.startAt + this.duration) * fps + delayFrames;
        }
        
        return Math.abs(triggerFrame - currentFrame) < 0.5; // Half-frame tolerance
    }

    /**
     * Get all sound effects that should be triggered at the current frame
     */
    getSoundEffectsToTrigger(currentFrame: number, fps: number): SoundConfig[] {
        const soundsToTrigger: SoundConfig[] = [];
        
        if (this.shouldTriggerEntranceSoundEffect(currentFrame, fps)) {
            soundsToTrigger.push(this.entranceSoundEffect!);
        }
        
        if (this.shouldTriggerExitSoundEffect(currentFrame, fps)) {
            soundsToTrigger.push(this.exitSoundEffect!);
        }
        
        // Handle custom sound effects
        for (const customSound of this.customSoundEffects) {
            const delayFrames = (customSound.timing?.delay ?? 0) * fps;
            const triggerFrame = this.startAt * fps + delayFrames;
            
            if (Math.abs(triggerFrame - currentFrame) < 0.5) {
                soundsToTrigger.push(customSound);
            }
        }
        
        return soundsToTrigger;
    }

    /**
     * Sound effects are handled centrally by the timeline's SoundPlayer component.
     * This method is kept for interface compatibility but implementations should not use it.
     * The timeline automatically renders all sound effects using Remotion's Audio component.
     */
    playSoundEffect(soundConfig: SoundConfig): void {
        // Intentionally empty - sound playback is handled by BasicTimeline.renderSoundEffects()
    }

    abstract render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults): React.ReactNode;
}

/**
 * Total visibility window for an animated item including animation buffers
 */
export interface VisibilityWindow {
    actualStart: number;    // When element first becomes visible (including entrance animation)
    actualEnd: number;      // When element completely disappears (including exit animation)
    totalDuration: number;  // actualEnd - actualStart
    scheduledStart: number; // Original startAt time
    scheduledEnd: number;   // Original startAt + duration
}

/**
 * Sound effect timing constraints within the visibility window
 */
export interface SoundConstraints {
    windowStart: number;      // Earliest possible sound start
    windowEnd: number;        // Latest possible sound end
    maxDuration: number;      // Maximum sound duration
    effectiveStart: number;   // Actual sound start time
    effectiveEnd: number;     // Actual sound end time
    willBeTrimmed: boolean;   // Whether sound exceeds window
    isValid: boolean;         // Whether configuration is valid
}

/**
 * Calculate the total visibility window for an animated item including animation buffers.
 * This determines the complete timespan during which sound effects can play.
 * Respects animation timing modes for accurate visibility calculation.
 * 
 * @param item - The animated item to calculate visibility for
 * @returns VisibilityWindow with actual start/end times including animation buffers
 */
export function calculateItemTotalVisibility(item: AnimatedItem): VisibilityWindow {
    let actualStart = item.startAt;
    let actualEnd = item.startAt + (item.duration ?? 0);
    
    // Calculate entrance animation impact on visibility
    if (item.entranceAnimation) {
        const animationDuration = item.entranceAnimation.timing.duration;
        const animationDelay = item.entranceAnimation.timing.delay ?? 0;
        const timingMode = item.entranceAnimation.timingMode ?? ANIMATION_TIMING_DEFAULTS.ENTRANCE;
        
        switch (timingMode) {
            case 'before-at':
                // Animation extends visibility BEFORE scheduled start
                actualStart = item.startAt - animationDuration - animationDelay;
                break;
            case 'at-after':
                // Animation extends visibility AFTER scheduled start (no change to actualStart)
                break;
            case 'centered':
                // Animation extends visibility both before and after start
                actualStart = item.startAt - (animationDuration / 2) - animationDelay;
                break;
        }
    }
    
    // Calculate exit animation impact on visibility
    if (item.exitAnimation && item.duration) {
        const animationDuration = item.exitAnimation.timing.duration;
        const animationDelay = item.exitAnimation.timing.delay ?? 0;
        const scheduledEnd = item.startAt + item.duration;
        const timingMode = item.exitAnimation.timingMode ?? ANIMATION_TIMING_DEFAULTS.EXIT;
        
        switch (timingMode) {
            case 'before-at':
                // Animation doesn't extend visibility beyond scheduled end (no change to actualEnd)
                break;
            case 'at-after':
                // Animation extends visibility AFTER scheduled end
                actualEnd = scheduledEnd + animationDuration + animationDelay;
                break;
            case 'centered':
                // Animation extends visibility both before and after end
                actualEnd = scheduledEnd + (animationDuration / 2) + animationDelay;
                break;
        }
    }
    
    return {
        actualStart,
        actualEnd,
        totalDuration: actualEnd - actualStart,
        scheduledStart: item.startAt,
        scheduledEnd: item.startAt + (item.duration ?? 0)
    };
}

/**
 * Calculate sound effect constraints based on animation timing and visibility window.
 * Ensures sound effects never exceed the element's total visibility duration.
 * 
 * @param item - The animated item
 * @param soundConfig - The sound effect configuration
 * @param soundType - Whether this is an entrance or exit sound effect
 * @returns SoundConstraints with timing boundaries and validation
 */
export function calculateSoundConstraints(
    item: AnimatedItem,
    soundConfig: SoundConfig,
    soundType: 'entrance' | 'exit'
): SoundConstraints {
    const visibility = calculateItemTotalVisibility(item);
    const delay = soundConfig.timing?.delay ?? 0;
    
    let windowStart: number;
    let windowEnd: number;
    
    if (soundType === 'entrance' && item.entranceAnimation) {
        // Entrance sound window: from animation start to total visibility end
        windowStart = visibility.actualStart;
        windowEnd = visibility.actualEnd;
    } else if (soundType === 'exit' && item.exitAnimation) {
        // Exit sound window: from exit animation start to when exit animation completes
        // Calculate exit animation start based on timing mode
        const animationDuration = item.exitAnimation.timing.duration;
        const animationDelay = item.exitAnimation.timing.delay ?? 0;
        const timingMode = item.exitAnimation.timingMode ?? ANIMATION_TIMING_DEFAULTS.EXIT;
        const scheduledEnd = visibility.scheduledEnd;
        
        let exitAnimationStart: number;
        let exitAnimationEnd: number;
        
        switch (timingMode) {
            case 'before-at':
                exitAnimationStart = scheduledEnd - animationDuration - animationDelay;
                exitAnimationEnd = scheduledEnd; // Animation completes BY scheduled end
                break;
            case 'at-after':
                exitAnimationStart = scheduledEnd + animationDelay;
                exitAnimationEnd = scheduledEnd + animationDuration + animationDelay; // Animation extends after
                break;
            case 'centered':
                exitAnimationStart = scheduledEnd - (animationDuration / 2) - animationDelay;
                exitAnimationEnd = scheduledEnd + (animationDuration / 2) - animationDelay; // Centered on end
                break;
        }
        
        windowStart = exitAnimationStart;
        // FIXED: Allow sound to play for the full animation duration, not just until visibility ends
        windowEnd = exitAnimationEnd;
    } else {
        // No animation - use scheduled time only
        windowStart = item.startAt;
        windowEnd = item.startAt + (item.duration ?? 0);
    }
    
    const effectiveStart = windowStart + delay;
    const maxDuration = Math.max(0, windowEnd - effectiveStart);
    const effectiveEnd = effectiveStart + maxDuration;
    
    // Debug logging for sound timing verification
    if (process.env.NODE_ENV === 'development') {
        console.log(`[SOUND_TIMING_FIX] ${item.id} ${soundType} sound constraints:`, {
            itemDuration: item.duration,
            animationDuration: soundType === 'exit' && item.exitAnimation ? item.exitAnimation.timing.duration : 
                               soundType === 'entrance' && item.entranceAnimation ? item.entranceAnimation.timing.duration : 0,
            timingMode: soundType === 'exit' && item.exitAnimation ? item.exitAnimation.timingMode ?? ANIMATION_TIMING_DEFAULTS.EXIT :
                        soundType === 'entrance' && item.entranceAnimation ? item.entranceAnimation.timingMode ?? ANIMATION_TIMING_DEFAULTS.ENTRANCE : 'none',
            windowStart: windowStart.toFixed(2),
            windowEnd: windowEnd.toFixed(2),
            effectiveStart: effectiveStart.toFixed(2),
            effectiveEnd: effectiveEnd.toFixed(2),
            maxDuration: maxDuration.toFixed(2),
            delay: delay.toFixed(2)
        });
    }
    
    const constraints = {
        windowStart,
        windowEnd,
        maxDuration,
        effectiveStart,
        effectiveEnd,
        willBeTrimmed: (soundConfig.metadata?.originalDuration ?? 0) > maxDuration,
        isValid: effectiveStart < windowEnd && maxDuration > 0
    };

    // Debug logging for constraint calculation (enable via environment variable)
    if (process.env.NODE_ENV === 'development' && process.env.DEBUG_SOUND_CONSTRAINTS === 'true') {
        console.log(`[SoundConstraints Debug] ${item.id} (${soundType}):`, {
            visibility,
            windowStart,
            windowEnd,
            maxDuration,
            effectiveStart,
            effectiveEnd,
            soundDuration: soundConfig.metadata?.originalDuration ?? 0,
            willBeTrimmed: constraints.willBeTrimmed
        });
    }

    return constraints;
}
